/**
 * SCRUM-162 — reviewable presence (check-in/out) history for one employee.
 * A paginated table sourced from GET /shifts (no new DB table). Used self-service
 * on the Profile page and per-employee on the Employee detail page.
 */
import { useState } from "react";
import {
  Card,
  Title,
  Table,
  Text,
  Badge,
  Group,
  Pagination,
  Center,
  Loader,
} from "@mantine/core";
import { IconFileTypeCsv } from "@tabler/icons-react";
import { usePresenceHistory, type Shift } from "../hooks/useShifts";
import { ActionMenu } from "../components/ActionMenu";

/** CSV-safe cell: wrap in quotes and double any internal quotes. */
function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function fmt(
  iso: string | null | undefined,
  tz: string | null | undefined,
  opts: Intl.DateTimeFormatOptions,
): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, {
    ...opts,
    ...(tz ? { timeZone: tz } : {}),
  }).format(new Date(iso));
}

function duration(inIso: string | null, outIso: string | null): string {
  if (!inIso || !outIso) return "—";
  const mins = Math.max(
    0,
    Math.floor(
      (new Date(outIso).getTime() - new Date(inIso).getTime()) / 60000,
    ),
  );
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
}

function StatusBadge({ shift }: { shift: Shift }) {
  if (!shift.clock_out_time) {
    return (
      <Badge color="teal" variant="light" size="sm">
        On duty
      </Badge>
    );
  }
  return (
    <Badge color="gray" variant="light" size="sm">
      Completed
    </Badge>
  );
}

const PAGE_SIZE = 15;

export function PresenceHistory({
  employeeId,
  title = "Check-in History",
}: {
  employeeId: string | null | undefined;
  title?: string;
}) {
  const { data: shifts, isLoading, isError } = usePresenceHistory(employeeId);
  const [page, setPage] = useState(1);

  const rows = shifts ?? [];
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  // Export the FULL history (every row, not just the current page) as CSV.
  const handleExportCsv = () => {
    const header = [
      "Date",
      "Facility",
      "Check-in",
      "Check-out",
      "Duration",
      "Status",
    ];
    const lines = rows.map((s) => {
      const tz = s.facilities?.timezone ?? null;
      return [
        fmt(s.clock_in_time, tz, {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
        s.facilities?.name ?? "",
        fmt(s.clock_in_time, tz, { hour: "numeric", minute: "2-digit" }),
        fmt(s.clock_out_time, tz, { hour: "numeric", minute: "2-digit" }),
        duration(s.clock_in_time, s.clock_out_time),
        s.clock_out_time ? "Completed" : "On duty",
      ]
        .map((c) => csvCell(c))
        .join(",");
    });
    const csv = [header.map(csvCell).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `check-in-history-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="md">
        <Title order={4}>{title}</Title>
        {rows.length > 0 && (
          <ActionMenu
            label="Export"
            items={[
              {
                key: "export-csv",
                label: "Export CSV",
                leftSection: <IconFileTypeCsv size={16} />,
                onClick: handleExportCsv,
              },
            ]}
          />
        )}
      </Group>

      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : isError ? (
        <Text size="sm" c="red">
          Couldn’t load the check-in history.
        </Text>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed" py="sm">
          No check-ins recorded yet.
        </Text>
      ) : (
        <>
          <Table.ScrollContainer minWidth={620}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Facility</Table.Th>
                  <Table.Th>Check-in</Table.Th>
                  <Table.Th>Check-out</Table.Th>
                  <Table.Th>Duration</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pageRows.map((s) => {
                  const tz = s.facilities?.timezone ?? null;
                  return (
                    <Table.Tr key={s.id}>
                      <Table.Td>
                        {fmt(s.clock_in_time, tz, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </Table.Td>
                      <Table.Td>{s.facilities?.name ?? "—"}</Table.Td>
                      <Table.Td>
                        {fmt(s.clock_in_time, tz, {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </Table.Td>
                      <Table.Td>
                        {fmt(s.clock_out_time, tz, {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </Table.Td>
                      <Table.Td>
                        {duration(s.clock_in_time, s.clock_out_time)}
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge shift={s} />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {totalPages > 1 && (
            <Group justify="space-between" mt="md" wrap="wrap">
              <Text size="xs" c="dimmed">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                {Math.min(currentPage * PAGE_SIZE, rows.length)} of{" "}
                {rows.length}
              </Text>
              <Pagination
                value={currentPage}
                onChange={setPage}
                total={totalPages}
                size="sm"
              />
            </Group>
          )}
        </>
      )}
    </Card>
  );
}
